import classNames from 'classnames';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';

const SELECTION_KEYS = ['Enter', ' ', 'Spacebar'];
const OPEN_KEYS = ['Enter', ' ', 'Spacebar', 'ArrowDown', 'ArrowUp'];

@observer
class DropdownSelect extends Component {
  toggleRef = React.createRef();

  pendingCloseReason = null;

  getDropdownRoot = (event) => {
    if (
      this.toggleRef.current &&
      typeof this.toggleRef.current.closest === 'function'
    ) {
      return this.toggleRef.current.closest('.DropdownSelect');
    }

    return (
      event?.currentTarget?.closest?.('.DropdownSelect') ||
      event?.target?.closest?.('.DropdownSelect') ||
      null
    );
  };

  focusToggle = () => {
    const toggle = this.toggleRef.current;
    if (toggle && typeof toggle.focus === 'function') {
      toggle.focus();
      return true;
    }

    return false;
  };

  handleMenuClickCapture = (event) => {
    const item = event?.target?.closest?.('.dropdown-item');
    if (!item || item.classList.contains('disabled')) {
      return;
    }

    this.pendingCloseReason = 'selection';
  };

  handleMenuKeyDownCapture = (event) => {
    const item = event?.target?.closest?.('.dropdown-item');
    if (!item || item.classList.contains('disabled')) {
      return;
    }

    if (SELECTION_KEYS.includes(event.key)) {
      this.pendingCloseReason = 'selection';
      return;
    }

    if (event.key === 'Escape') {
      this.pendingCloseReason = 'dismiss';
      return;
    }

    if (event.key === 'Tab') {
      this.pendingCloseReason = null;
    }
  };

  handleToggle = (event, isOpen) => {
    const { focusSelectedItemOnKeyboardOpen, focusToggleOnClose } = this.props;
    const dropdownRoot = this.getDropdownRoot(event);

    const isKeyboardOpenTrigger =
      event?.type === 'keydown' && OPEN_KEYS.includes(event.key);
    if (focusSelectedItemOnKeyboardOpen && isOpen && isKeyboardOpenTrigger) {
      setTimeout(() => {
        // Nested dropdowns change Reactstrap's default focus target, so explicitly
        // re-focus the currently selected option when opening via keyboard.
        const menu = dropdownRoot?.querySelector('.dropdown-menu');
        const selectedItem =
          menu?.querySelector('.dropdown-item[aria-current="true"]') ||
          menu?.querySelector('.dropdown-item.active');
        const fallbackItem = menu
          ? Array.from(
              menu.querySelectorAll('.dropdown-item:not(.disabled)')
            )[0]
          : undefined;
        const itemToFocus = selectedItem || fallbackItem;
        if (itemToFocus && typeof itemToFocus.focus === 'function') {
          itemToFocus.focus();
        }
      }, 0);
    }

    const shouldRestoreFocus =
      !isOpen &&
      focusToggleOnClose &&
      ['selection', 'dismiss'].includes(this.pendingCloseReason);
    if (shouldRestoreFocus) {
      setTimeout(() => {
        this.focusToggle();
      }, 0);
    }

    if (!isOpen) {
      this.pendingCloseReason = null;
    }

    if (typeof this.props.onToggle === 'function') {
      this.props.onToggle(event, isOpen);
    }
  };

  render() {
    const {
      label,
      children,
      disabled,
      isLarge,
      className,
      focusSelectedItemOnKeyboardOpen,
      focusToggleOnClose,
      onToggle,
      ...props
    } = this.props;

    const styles = isLarge
      ? {
          overflowY: 'scroll',
          maxHeight: '500px',
        }
      : {};

    return (
      <UncontrolledDropdown
        className={classNames('DropdownSelect', className)}
        disabled={disabled}
        onToggle={this.handleToggle}
        {...props}
      >
        <DropdownToggle caret disabled={disabled} innerRef={this.toggleRef}>
          {label}
        </DropdownToggle>
        <DropdownMenu
          style={styles}
          onClickCapture={this.handleMenuClickCapture}
          onKeyDownCapture={this.handleMenuKeyDownCapture}
        >
          {children}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }
}

DropdownSelect.propTypes = {
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  isLarge: PropTypes.bool,
  className: PropTypes.string,
  focusSelectedItemOnKeyboardOpen: PropTypes.bool,
  focusToggleOnClose: PropTypes.bool,
  onToggle: PropTypes.func,
};

DropdownSelect.defaultProps = {
  isLarge: false,
  focusSelectedItemOnKeyboardOpen: false,
  focusToggleOnClose: false,
};

export default DropdownSelect;
